export type TableSection = 'header' | 'body';

export type CellPosition = {
    section: TableSection;
    rowIndex: number;
    colIndex: number;
};

export type SelectionRange = {
    start: CellPosition;
    end: CellPosition;
};

type SelectionRect = {
    rowStart: number;
    rowEnd: number;
    colStart: number;
    colEnd: number;
};

/**
 * Maps a cell position to a unified "global" row index that spans both
 * sections: header row `i` occupies global row `i`, and body row `i` occupies
 * global row `i + headerRowCount`. This lets header + body cells share a single
 * rectangular coordinate space (used by cross-section selection and copy).
 *
 * `headerRowCount` é obrigatório de propósito. Um default de 1 faria cada call
 * site esquecido compilar e mapear errado só nas tabelas com faixa — e é errado
 * já no empate: com uma faixa, a folha e a primeira linha do corpo cairiam
 * ambas na linha global 1, e `normalizeSelection` ordenaria pelo endpoint que
 * por acaso fosse `start`.
 */
export const toGlobalRow = (position: CellPosition, headerRowCount: number): number =>
    position.section === 'header' ? position.rowIndex : position.rowIndex + headerRowCount;

/** Inverse of {@link toGlobalRow}: resolves a global row back to its section. */
export const globalRowToPosition = (
    globalRow: number,
    headerRowCount: number,
): { section: TableSection; rowIndex: number } =>
    globalRow < headerRowCount
        ? { section: 'header', rowIndex: globalRow }
        : { section: 'body', rowIndex: globalRow - headerRowCount };

export const normalizeSelection = (
    selection: SelectionRange,
    headerRowCount: number,
): SelectionRange => {
    const { start, end } = selection;
    // Order endpoints by global row so `start` is always the upper (lower
    // global row) cell. For same-section selections this reduces to the
    // classic Math.min/max ordering; for cross-section it canonically yields
    // start = header, end = body.
    const [upper, lower] =
        toGlobalRow(start, headerRowCount) <= toGlobalRow(end, headerRowCount)
            ? [start, end]
            : [end, start];
    const colStart = Math.min(start.colIndex, end.colIndex);
    const colEnd = Math.max(start.colIndex, end.colIndex);
    return {
        start: { ...upper, colIndex: colStart },
        end: { ...lower, colIndex: colEnd },
    };
};

export const getSelectionRect = (
    selection: SelectionRange,
    headerRowCount: number,
): SelectionRect => {
    const normalized = normalizeSelection(selection, headerRowCount);
    return {
        rowStart: normalized.start.rowIndex,
        rowEnd: normalized.end.rowIndex,
        colStart: normalized.start.colIndex,
        colEnd: normalized.end.colIndex,
    };
};

/**
 * Like {@link getSelectionRect} but rows are expressed in unified global-row
 * space (see {@link toGlobalRow}), so the rectangle can span header + body.
 */
export const getSelectionRectGlobal = (
    selection: SelectionRange,
    headerRowCount: number,
): SelectionRect => {
    const normalized = normalizeSelection(selection, headerRowCount);
    return {
        rowStart: toGlobalRow(normalized.start, headerRowCount),
        rowEnd: toGlobalRow(normalized.end, headerRowCount),
        colStart: normalized.start.colIndex,
        colEnd: normalized.end.colIndex,
    };
};

export const isCellInSelection = (
    position: CellPosition,
    selection: SelectionRange | null,
    headerRowCount: number,
): boolean => {
    if (!selection) return false;

    const rect = getSelectionRectGlobal(selection, headerRowCount);
    const globalRow = toGlobalRow(position, headerRowCount);
    return (
        globalRow >= rect.rowStart &&
        globalRow <= rect.rowEnd &&
        position.colIndex >= rect.colStart &&
        position.colIndex <= rect.colEnd
    );
};

export const expandSelectionToMerged = (
    selection: SelectionRange,
    getCellAt: (
        section: TableSection,
        rowIndex: number,
        colIndex: number,
    ) => {
        id: string;
        rowSpan?: number;
        colSpan?: number;
        mergedInto?: string;
    } | null,
    getOriginPosition: (cellId: string) => CellPosition | null,
    headerRowCount: number,
): SelectionRange => {
    let nextSelection = normalizeSelection(selection, headerRowCount);
    let hasChanges = true;

    // Merges never cross the header/body boundary, so iterating in unified
    // global-row space is safe: a header cell stays at global row 0 and body
    // cells stay within their own section.
    while (hasChanges) {
        hasChanges = false;
        const rect = getSelectionRectGlobal(nextSelection, headerRowCount);

        for (let globalRow = rect.rowStart; globalRow <= rect.rowEnd; globalRow += 1) {
            const { section, rowIndex } = globalRowToPosition(globalRow, headerRowCount);
            for (let col = rect.colStart; col <= rect.colEnd; col += 1) {
                const cell = getCellAt(section, rowIndex, col);
                if (!cell) continue;

                const originId = cell.mergedInto || cell.id;
                const originPos = getOriginPosition(originId);
                if (!originPos) continue;

                const originCell = getCellAt(
                    originPos.section,
                    originPos.rowIndex,
                    originPos.colIndex,
                );
                if (!originCell) continue;

                const rowSpan = originCell.rowSpan || 1;
                const colSpan = originCell.colSpan || 1;

                const originGlobalRow = toGlobalRow(originPos, headerRowCount);
                const originGlobalRowEnd = originGlobalRow + rowSpan - 1;
                const originColEnd = originPos.colIndex + colSpan - 1;

                const newStartRow = Math.min(rect.rowStart, originGlobalRow);
                const newStartCol = Math.min(rect.colStart, originPos.colIndex);
                const newEndRow = Math.max(rect.rowEnd, originGlobalRowEnd);
                const newEndCol = Math.max(rect.colEnd, originColEnd);

                if (
                    newStartRow !== rect.rowStart ||
                    newStartCol !== rect.colStart ||
                    newEndRow !== rect.rowEnd ||
                    newEndCol !== rect.colEnd
                ) {
                    const start = globalRowToPosition(newStartRow, headerRowCount);
                    const end = globalRowToPosition(newEndRow, headerRowCount);
                    nextSelection = {
                        start: { ...start, colIndex: newStartCol },
                        end: { ...end, colIndex: newEndCol },
                    };
                    hasChanges = true;
                }
            }
        }
    }

    return nextSelection;
};
